import React from 'react';

/**
 * Represents a single step in a help guide
 */
export interface HelpStep {
    text: string;
    imageUrl?: string;
}

/**
 * Structured help content with multiple steps
 * 
 * Example usage:
 * ```
 * const tokenHelp: StructuredHelp = {
 *   steps: [
 *     {
 *       text: "Navigate to your Azure DevOps organization settings.",
 *       imageUrl: "/images/help/azure-devops-step1.png"
 *     },
 *     {
 *       text: "Click on 'Personal Access Tokens' in the security section.",
 *       imageUrl: "/images/help/azure-devops-step2.png"
 *     },
 *     {
 *       text: "Click 'New Token' and configure the required permissions.",
 *       imageUrl: "/images/help/azure-devops-step3.png"
 *     }
 *   ]
 * }
 * ```
 */
export interface StructuredHelp {
    steps: HelpStep[];
}

/**
 * Help text can be either a simple string or structured help with multiple steps
 */
export type HelpText = string | StructuredHelp;

interface HelpModalProps {
    isOpen: boolean;
    onClose: () => void;
    helpText: HelpText;
    title?: string;
}

const HelpModal: React.FC<HelpModalProps> = ({ isOpen, onClose, helpText, title = "Help" }) => {
    if (!isOpen) return null;

    // Parse helpText if it's a JSON string
    let parsedHelpText: HelpText = helpText;
    if (typeof helpText === 'string') {
        try {
            // Try to parse as JSON
            const parsed = JSON.parse(helpText);
            if (parsed && typeof parsed === 'object' && parsed.steps) {
                parsedHelpText = parsed;
            }
        } catch {
            // If parsing fails, treat as simple string
            parsedHelpText = helpText;
        }
    }

    const isStructured = typeof parsedHelpText === 'object' && parsedHelpText.steps;
    const steps = isStructured ? (parsedHelpText as StructuredHelp).steps : [{ text: parsedHelpText as string }];

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
        }}>
            <div style={{
                backgroundColor: 'white',
                borderRadius: 8,
                maxWidth: 600,
                maxHeight: '80vh',
                width: '90%',
                position: 'relative',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                display: 'flex',
                flexDirection: 'column'
            }}>
                {/* Header */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: '1px solid #eee',
                    padding: '24px 24px 16px',
                    flexShrink: 0
                }}>
                    <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>{title}</h3>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'none',
                            border: 'none',
                            fontSize: 24,
                            cursor: 'pointer',
                            color: '#666',
                            padding: 4,
                            lineHeight: 1,
                            borderRadius: 4
                        }}
                        onMouseOver={(e) => (e.target as HTMLElement).style.backgroundColor = '#f0f0f0'}
                        onMouseOut={(e) => (e.target as HTMLElement).style.backgroundColor = 'transparent'}
                    >
                        ×
                    </button>
                </div>

                {/* Content */}
                <div style={{ overflowY: 'auto', padding: '20px 24px' }}>
                    {steps.map((step, index) => (
                        <div key={index} style={{ marginBottom: steps.length > 1 ? 24 : 0 }}>
                            {steps.length > 1 && (
                                <h4 style={{
                                    margin: '0 0 12px 0',
                                    fontSize: 16,
                                    color: '#333',
                                    fontWeight: 600
                                }}>
                                    Step {index + 1}
                                </h4>
                            )}

                            <p style={{
                                margin: '0 0 12px 0',
                                lineHeight: 1.5,
                                color: '#555',
                                whiteSpace: 'break-spaces'
                            }}>
                                {step.text}
                            </p>

                            {step.imageUrl && (
                                <div style={{
                                    marginTop: 12,
                                    textAlign: 'center',
                                    border: '1px solid #eee',
                                    borderRadius: 4,
                                    padding: 8,
                                    backgroundColor: '#fafafa'
                                }}>
                                    <img
                                        src={step.imageUrl}
                                        alt={`Step ${index + 1} illustration`}
                                        style={{
                                            maxWidth: '100%',
                                            height: 'auto',
                                            borderRadius: 4
                                        }}
                                        onError={(e) => {
                                            (e.target as HTMLImageElement).style.display = 'none';
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {/* Footer */}
                <div style={{
                    textAlign: 'right',
                    borderTop: '1px solid #eee',
                    padding: '16px 24px 24px',
                    flexShrink: 0
                }}>
                    <button
                        onClick={onClose}
                        style={{
                            padding: '8px 16px',
                            background: 'linear-gradient(115deg, rgb(34, 34, 69) 25%, rgb(-77, 182, 159) 162%)',
                            color: 'white',
                            border: 'none',
                            borderRadius: 4,
                            cursor: 'pointer'
                        }}
                        onMouseOver={(e) => (e.target as HTMLElement).style.backgroundColor = '#1976d2'}
                        onMouseOut={(e) => (e.target as HTMLElement).style.backgroundColor = '#2196f3'}
                    >
                        Got it
                    </button>
                </div>
            </div>
        </div>
    );
};

export default HelpModal;